import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { FexidocLogo } from "@/components/FexidocLogo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", plan: null, error: null });

  useEffect(() => {
    if (!sessionId) { setState({status:"error", error:"Missing session id"}); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setState({status:"paid", plan: data.plan}); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState({status:"failed", error:"Payment did not complete"}); return; }
        if (attempts >= 20) { setState({status:"pending"}); return; }
        setTimeout(tick, 2000);
      } catch (e) {
        if (attempts >= 20) { setState({status:"error", error: e?.response?.data?.detail || "Could not check payment status"}); return; }
        setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="max-w-md w-full text-center">
        <div className="mb-8"><FexidocLogo size={32}/></div>
        <div className="fx-panel p-10">
          {state.status === "polling" && (
            <>
              <Loader2 size={40} className="mx-auto text-cyan-400 animate-spin" data-testid="payment-polling"/>
              <div className="font-display text-xl font-bold mt-4">Confirming your payment…</div>
              <div className="text-sm text-slate-400 mt-2">This usually takes a few seconds.</div>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 size={40} className="mx-auto text-emerald-400" data-testid="payment-success"/>
              <div className="font-display text-2xl font-bold mt-4">Subscription active</div>
              <div className="text-sm text-slate-400 mt-2">Welcome to Fexidoc <span className="text-slate-200 capitalize">{state.plan}</span>. You can start using higher limits immediately.</div>
              <Link to="/app" className="mt-6 inline-block fx-btn-primary text-white px-5 py-2.5 rounded-lg font-medium" data-testid="back-to-app">Back to app</Link>
            </>
          )}
          {(state.status === "failed" || state.status === "error") && (
            <>
              <AlertCircle size={40} className="mx-auto text-red-400" data-testid="payment-error"/>
              <div className="font-display text-xl font-bold mt-4">Payment didn't complete</div>
              <div className="text-sm text-slate-400 mt-2">{state.error || "Try again from the billing page."}</div>
              <Link to="/app/billing" className="mt-6 inline-block border border-white/10 hover:border-white/20 text-slate-200 px-5 py-2.5 rounded-lg">Back to billing</Link>
            </>
          )}
          {state.status === "pending" && (
            <>
              <Loader2 size={40} className="mx-auto text-amber-400" data-testid="payment-pending"/>
              <div className="font-display text-xl font-bold mt-4">Still processing</div>
              <div className="text-sm text-slate-400 mt-2">Your payment is still being processed. We'll update your subscription automatically when it clears.</div>
              <Link to="/app/billing" className="mt-6 inline-block border border-white/10 hover:border-white/20 text-slate-200 px-5 py-2.5 rounded-lg">Back to billing</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
